/**
 * MetricsReport — Reports → Metrics. First the recruiter speed to lead
 * (RecruiterSpeedReport: how long a PERSON took to reach an eligible or
 * needs-review lead once it was the team's turn, in business or all hours;
 * owner request 2026-10-01), then the headline sales KPIs for the leads that
 * were CREATED in the selected period (a true cohort): reach rate, call
 * connect rate, lead→randomized, and pipeline cycle time. Scoped by the global
 * study filter. Plain SummaryStat cards — no chart library.
 *
 * The old "Speed to lead" card (sales_summary_stats' speed_* fields: the first
 * outbound message of any kind, which is the automated welcome) is no longer
 * shown: it read as seconds for every lead. The RPC still returns the fields.
 */
import { useEffect, useState } from 'react';
import api from '../../../services/api';
import { SalesSummary } from '../../types';
import RecruiterSpeedReport from './RecruiterSpeedReport';
import { SummaryStat } from './StatBlocks';

interface Props {
  from: string | null;
  to: string | null;
  studyId?: string | null;
}

const pct = (v: number | null) => (v === null || v === undefined ? '—' : `${v}%`);
const days = (v: number | null) => (v === null || v === undefined ? '—' : `${v.toFixed(1)} d`);

export default function MetricsReport({ from, to, studyId = null }: Props) {
  const [s, setS] = useState<SalesSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get('/api/crm/analytics/sales-summary', {
        params: { from: from || undefined, to: to || undefined, study_id: studyId || undefined },
      })
      .then((res) => {
        if (alive) {
          setS(res.data?.summary || null);
          setError(null);
        }
      })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.error || 'Failed to load metrics');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to, studyId]);

  let pipeline: JSX.Element;
  if (loading) pipeline = <p className="p-1 text-sm text-gray-400">Loading metrics…</p>;
  else if (error) pipeline = <p className="p-1 text-sm text-red-600 dark:text-red-400">{error}</p>;
  else if (!s || s.leads === 0)
    pipeline = <p className="p-1 text-sm text-gray-500 dark:text-gray-400">No leads created in this range yet.</p>;
  else pipeline = (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <SummaryStat label="Leads (cohort)" value={s.leads.toLocaleString()} sub="created in range" />
        <SummaryStat
          label="Reach rate"
          value={pct(s.reach_pct)}
          sub={`${s.reached.toLocaleString()} ever replied`}
        />
        <SummaryStat
          label="Call connect rate"
          value={pct(s.connect_pct)}
          sub={`${s.connected_calls.toLocaleString()} / ${s.outbound_calls.toLocaleString()} calls`}
        />
        <SummaryStat
          label="Lead → randomized"
          value={pct(s.lead_to_rand_pct)}
          sub={`${s.randomized.toLocaleString()} randomized`}
          highlight={s.randomized > 0}
        />
        <SummaryStat
          label="Cycle time (median)"
          value={days(s.cycle_median_days)}
          sub="lead → randomized"
        />
      </div>
      <p className="px-1 text-[11px] leading-snug text-gray-400">
        Cohort = leads created in the selected range. Booking/show/screen-fail rates and per-stage velocity live in
        the Conversion tab.
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      <RecruiterSpeedReport from={from} to={to} studyId={studyId} />
      <section aria-labelledby="pipeline-metrics-title" className="space-y-3">
        <h2 id="pipeline-metrics-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Pipeline
        </h2>
        {pipeline}
      </section>
    </div>
  );
}
